<!--
  - Copyright (c) 2022. Haodong JU
  -->

<template>
  <v-container class="px-6">
    <v-progress-linear
      v-if="loading"
      color="primary"
      indeterminate
    />
    <page-header>
      {{ $t('vouchers') }}
    </page-header>


    <div
      class="d-flex align-center"
    >
      <v-item-group
        v-model="tab"
        mandatory
      >
        <div class="d-flex">
          <v-item v-slot="{active,toggle}">
            <v-btn
              :color="active?'primary':''"
              :outlined="!active"
              class="mr-2"
              style="background: white"
              @click="toggle"
            >
              {{ $t('vouchersManage') }}
            </v-btn>
          </v-item>
          <v-item v-slot="{active,toggle}">
            <v-btn
              :color="active?'primary':''"
              :outlined="!active"
              class="mr-2"
              style="background: white"
              @click="toggle"
            >
              {{ $t('alreadySaledVouchers') }}
            </v-btn>
          </v-item>
        </div>
      </v-item-group>
    </div>

    <v-tabs-items
      v-model="tab"
      class="mt-4"
    >
      <v-tab-item>
        <v-container>
          <div
            style="display: grid;
              grid-template-columns: repeat(4,minmax(0,1fr));
              grid-gap: 24px"
          >
            <v-card
              class="pa-4 d-flex align-center justify-center"
              color="#eee"
              elevation="0"
              height="320"
              @click="startEditCoupon(-1)"
            >
              <div class="text-center">
                <v-icon size="48">
                  mdi-plus
                </v-icon>
                <div>
                  {{ $t('addNewVouchers') }}
                </div>
              </div>
            </v-card>
            <coupon-info-card
              v-for="(c,i) in couponList"
              :key="c.dishId"
              :coupon-info="c"
              @click="startEditCoupon(i)"
              @delete="deleteAndReload(c.dishId)"
            />
          </div>
        </v-container>
      </v-tab-item>
      <v-tab-item>
        <ik-data-table
          :custom-on-row-click="showCouponDetail"
          :entity-name=" $t('alreadySaledVouchers')"
          :model="model"
          :required-date-value="dates"
          :show-title="false"
          :use-action="false"
          :use-date-filter="true"
          :use-default-action="false"
          :use-select="false"
          icon="mdi-cards"
        >
          <template #right="{tableItems}">
            <v-card
              class="px-6 py-2 mr-4 text-body-2"
              color="primary"
              dark
              outlined
            >
              {{
                $t('totalMoney')
              }}：{{ tableItems.reduce((sum, it) => sum + parseFloat(it.leftAmount), 0) | priceDisplay }}
            </v-card>
          </template>
        </ik-data-table>
      </v-tab-item>
    </v-tabs-items>

    <nice-dialog v-model="showCouponDetailDialog">
      <v-container
        v-if="couponDetail"
        class="px-6"
        style="height: 98vh"
      >
        <page-header>{{ $t('vouchersUseRecord') }}</page-header>
        <div class="text-h5 font-weight-bold pl-2">
          {{ $t('voucherCode') }}:{{ couponDetail.longId }}
        </div>
        <span class="font-weight-regular pl-2 mt-2">{{
          couponDetail.leftAmount | priceDisplay
        }}/{{ couponDetail.totalAmount | priceDisplay }}</span>

        <div class="text-body-1 pl-2">
          {{ $t('creatIn') }}{{ couponDetail.createdAt | beautifulTime }}
        </div>
        <v-simple-table
          v-if="couponDetail.consumeLog.length>0"
          class="mt-4"
        >
          <thead>
            <tr>
              <th>{{ $t('orderNumber') }}</th>
              <th>{{ $t('time') }}</th>
              <th>{{ $t('useTheAmountOf') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="i in couponDetail.consumeLog"
              :key="i.id"
            >
              <td>#{{ i.orderId }}</td>
              <td>{{ i.createdAt | beautifulTime }}</td>
              <td>{{ i.amount | priceDisplay }}</td>
            </tr>
          </tbody>
        </v-simple-table>

        <template v-else>
          <div class="pl-2 mt-4">
            {{ $t('noUsed') }}
          </div>
        </template>
      </v-container>
    </nice-dialog>
    <ik-entity-form
      v-model="showEditCouponDialog"
      :edited-index="activeCouponIndex"
      :model="CouponModel"
      :title="$t('editVouchers')"
      @need-refresh="reload"
    />
  </v-container>
</template>

<script>

  import { IkDataTable, IkEntityForm } from 'metaflow-js'
  import MemberCardRecord, { getCouponRecord } from '../../../model/bills/CouponSalesRecord'
  import NiceDialog from '@/composable/InK/NiceDialog'
  import PageHeader from '@/composable/InK/typography/PageHeader'
  import { today } from '@/model/common/DateRepository'
  import CouponInfoCard from '@/views/AadenPages/customer/CouponInfoCard'
  import Coupon from '@/model/Coupon'
  import { predefinedDateRangeList } from 'metaflow-js/EntityFrameWork/DateRepository'

  export default {
    components: {
      CouponInfoCard,
      PageHeader,
      NiceDialog,
      IkDataTable,
      IkEntityForm,
    },
    data () {
      return {
        tab: 0,
        activeCouponIndex: null,
        couponList: [],
        CouponModel: Coupon,
        model: MemberCardRecord,
        couponDetail: null,
        loading: false,
        showCouponDetailDialog: null,
        dates: [today, today],
        showEditCouponDialog: null,
      }
    },
    mounted () {
      this.dates = predefinedDateRangeList.at(-1).dateRange()
      this.reload()
    },
    methods: {
      async deleteAndReload (dishId) {
        await Coupon.remove(dishId)
        await this.reload()
      },
      startEditCoupon (index) {
        this.showEditCouponDialog = true
        this.activeCouponIndex = index
      },
      async showCouponDetail (coupon) {
        this.couponDetail = await getCouponRecord(coupon.id)
        this.showCouponDetailDialog = true
      },
      async reload () {
        this.loading = true
        this.couponList = (await Coupon.getList(true)).filter(it => it.code !== 'gk' && it.code !== 'gel')
        this.loading = false
      },
    },
  }
</script>
